import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowDownRight, ArrowUpRight } from 'lucide-react'
import { api } from '@/lib/api'
import { formatDateTime } from '@/lib/date'
import { money } from '@/lib/money'
import { tierIcon, tierTint } from '@/lib/tiers'
import type { WalletInfo, WalletTx } from '@/types'
import Card, { SectionLabel } from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import LogoLoader from '@/components/ui/LogoLoader'
import { cn } from '@/lib/cn'

export default function Wallet() {
  const navigate = useNavigate()

  const wallet = useQuery({
    queryKey: ['wallet'],
    queryFn: async () => (await api.get<WalletInfo>('/wallet')).data,
  })

  const txs = useQuery({
    queryKey: ['wallet-tx'],
    queryFn: async () => (await api.get<{ data: WalletTx[] }>('/wallet/transactions')).data.data,
  })

  if (wallet.isLoading) return <LogoLoader />

  const info = wallet.data
  const currentThreshold = info?.tier?.topup_threshold ?? 0
  const lifetime = info?.lifetime_topup ?? 0
  // A tier set by hand can sit above what lifetime top-up earned, so the next
  // tier is the first one above whichever is higher - never one below the
  // tier already held. A locked tier is not moved by top-ups at all.
  const standing = Math.max(lifetime, currentThreshold)
  const nextTier = info?.tier_locked ? undefined : info?.tiers.find((t) => t.topup_threshold > standing)
  const progress = nextTier ? Math.min(100, Math.round((lifetime / nextTier.topup_threshold) * 100)) : 100

  return (
    <div>
      <PageHeader
        title="Wallet & tier"
        subtitle="Top up to unlock cheaper flat prices per product"
        actions={<Button onClick={() => navigate('/account/wallet/topup')}>Top up</Button>}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <SectionLabel>Balance</SectionLabel>
          <p className="text-3xl font-extrabold text-ink">{money(info?.balance)}</p>
          <p className="mt-1 text-xs text-muted">Lifetime top-up: {money(info?.lifetime_topup)}</p>
        </Card>
        <Card>
          <SectionLabel>Price tier</SectionLabel>
          <p className="flex items-center gap-2 text-xl font-extrabold">
            <span className="flex size-8 items-center justify-center rounded-full border" style={tierTint(info?.tier?.color)}>
              {(() => {
                const TierIcon = tierIcon(info?.tier?.icon)
                return <TierIcon className="size-4" />
              })()}
            </span>
            <span style={info?.tier?.color ? { color: info.tier.color } : undefined}>{info?.tier?.name ?? 'Member'}</span>
          </p>
          {nextTier ? (
            <>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-ink/10">
                <div
                  className="h-full rounded-full bg-brand-500"
                  style={{ width: `${progress}%`, ...(nextTier.color ? { backgroundColor: nextTier.color } : {}) }}
                />
              </div>
              <p className="mt-1.5 text-xs text-muted">
                <Link to="/account/wallet/topup" className="underline hover:text-ink">
                  Top up {money(nextTier.topup_threshold - lifetime)} more
                </Link>{' '}
                (lifetime) to unlock <span className="font-bold text-ink">{nextTier.name}</span> prices
              </p>
            </>
          ) : info?.tier_locked ? (
            <p className="mt-2 text-xs text-muted">Your tier was set by MPG. Top-ups do not change it for now.</p>
          ) : (
            <p className="mt-2 text-xs font-semibold text-status-success">Highest tier unlocked - best flat prices.</p>
          )}
          <p className="mt-2 text-2xs text-muted">
            Current tier threshold: {money(currentThreshold)} lifetime top-up
          </p>
        </Card>
      </div>

      <h2 className="mb-3 mt-8 text-sm font-bold uppercase tracking-wide text-muted">Statement</h2>
      <Card className="p-0">
        <div className="divide-y divide-line">
          {txs.data?.length === 0 && <p className="p-6 text-center text-sm text-muted">No transactions yet.</p>}
          {txs.data?.map((tx) => (
            <div key={tx.id} className="flex items-center justify-between px-5 py-3 text-sm">
              <div className="flex items-center gap-3">
                <span
                  className={cn(
                    'flex size-8 items-center justify-center rounded-full',
                    tx.type === 'credit' ? 'border border-success/60 bg-success/20 text-success' : 'border border-danger/60 bg-danger/20 text-danger',
                  )}
                >
                  {tx.type === 'credit' ? <ArrowDownRight className="size-4" /> : <ArrowUpRight className="size-4" />}
                </span>
                <div>
                  <p className="font-semibold">{tx.description}</p>
                  <p className="text-2xs text-muted">{formatDateTime(tx.at)}</p>
                </div>
              </div>
              <div className="text-right">
                <p className={cn('font-bold', tx.type === 'credit' ? 'text-status-success' : 'text-status-danger')}>
                  {tx.type === 'credit' ? '+' : '-'}
                  {money(tx.amount)}
                </p>
                <p className="text-2xs text-muted">Balance {money(tx.balance_after)}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}
